import { supabase } from '@/lib/supabase';
import type { Transaction } from '@/lib/supabase';

export async function getTransactions(userId: string): Promise<Transaction[]> {
  const { data, error } = await supabase
    .from('transactions')
    .select('*')
    .eq('user_id', userId)
    .order('date', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function insertTransactions(transactions: Omit<Transaction, 'id' | 'created_at' | 'updated_at'>[]): Promise<Transaction[]> {
  const { data, error } = await supabase
    .from('transactions')
    .insert(transactions)
    .select();

  if (error) throw error;
  return data || [];
}

export async function updateTransaction(id: string, updates: Partial<Transaction>): Promise<Transaction> {
  const { data, error } = await supabase
    .from('transactions')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteTransaction(id: string): Promise<void> {
  const { error } = await supabase
    .from('transactions')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function deleteTransactions(ids: string[]): Promise<void> {
  const { error } = await supabase
    .from('transactions')
    .delete()
    .in('id', ids);

  if (error) throw error;
}

export async function getTransactionSummary(userId: string) {
  const transactions = await getTransactions(userId);
  
  const incomeTransactions = transactions.filter(t => t.transaction_type === 'Income');
  const expenseTransactions = transactions.filter(t => t.transaction_type === 'Expense');
  const transferTransactions = transactions.filter(t => t.transaction_type === 'Transfer');
  const refundTransactions = transactions.filter(t => t.transaction_type === 'Refund');

  const totalIncome = incomeTransactions.reduce((sum, t) => sum + Math.abs(Number(t.amount)), 0);
  const totalExpenses = expenseTransactions.reduce((sum, t) => sum + Math.abs(Number(t.amount)), 0);
  const totalTransfers = transferTransactions.reduce((sum, t) => sum + Math.abs(Number(t.amount)), 0);
  const totalRefunds = refundTransactions.reduce((sum, t) => sum + Math.abs(Number(t.amount)), 0);

  const netCashFlow = totalIncome - totalExpenses;
  
  // Transaction type counts for audit panel
  const typeCounts = {
    income: incomeTransactions.length,
    expense: expenseTransactions.length,
    transfer: transferTransactions.length,
    refund: refundTransactions.length,
  };

  const byCategory = transactions.reduce((acc, t) => {
    const cat = t.category;
    if (!acc[cat]) acc[cat] = { income: 0, expense: 0 };
    if (t.transaction_type === 'Income') acc[cat].income += Number(t.amount);
    else if (t.transaction_type === 'Expense') acc[cat].expense += Math.abs(Number(t.amount));
    return acc;
  }, {} as Record<string, { income: number; expense: number }>);

  const byBusiness = transactions
    .filter(t => t.source === 'Business' && t.business_name)
    .reduce((acc, t) => {
      const biz = t.business_name!;
      if (!acc[biz]) acc[biz] = { income: 0, expense: 0 };
      if (t.transaction_type === 'Income') acc[biz].income += Number(t.amount);
      else if (t.transaction_type === 'Expense') acc[biz].expense += Math.abs(Number(t.amount));
      return acc;
    }, {} as Record<string, { income: number; expense: number }>);

  const recurringExpenses = transactions
    .filter(t => t.recurring && t.transaction_type === 'Expense')
    .reduce((sum, t) => sum + Math.abs(Number(t.amount)), 0);

  const taxDeductible = transactions
    .filter(t => t.tax_deductible)
    .reduce((sum, t) => sum + Math.abs(Number(t.amount)), 0);

  return {
    totalIncome,
    totalExpenses,
    totalTransfers,
    totalRefunds,
    netCashFlow,
    typeCounts,
    byCategory,
    byBusiness,
    recurringExpenses,
    taxDeductible,
    transactions,
  };
}
